import type { LexicalEditor } from 'lexical';

import type { LanguageEnum } from 'js/src/client';
import type { StoreORMInterface } from 'js/src/data/store-orm';
import StoreORM from 'js/src/data/store-orm';
import type { TranslatedEditorsType } from 'js/src/data/translated-editors/translated-editors-types';
import type { RootState } from 'js/src/libs/RootState';

export function getTranslatedEditors(state: RootState) {
    return state.transcript_translations.translated_editors;
}

// currently we only ever interact with 1 lang code so this selector is just convenience
// if we move toward storing multiple translations we should write a different
// selector for that purpose
export function getTranslatedLangCodeFromSceneGroupId(
    state: RootState,
    sceneGroupId: string,
): LanguageEnum | undefined {
    return _getLangCodeFromSceneGroupId(getTranslatedEditors(state), sceneGroupId);
}

function _getLangCodeFromSceneGroupId(
    translatedEditors: StoreORMInterface<TranslatedEditorsType, string>,
    sceneGroupId: string,
): LanguageEnum | undefined {
    const orm = new StoreORM<TranslatedEditorsType, string>(translatedEditors);
    const translations = orm.getByIdOrUndefined(sceneGroupId);
    if (translations) {
        return translations.get('translations').keySeq().get(0);
    }
}

export function getAllTranslatedEditorsSceneGroupIds(state: RootState) {
    const orm = new StoreORM(getTranslatedEditors(state));
    return orm.getAllIds();
}

export function getSceneGroupIdsWithTranslation(state: RootState) {
    const orm = new StoreORM(getTranslatedEditors(state));
    return orm.getAllIds();
}

export function getActiveTranslationSceneGroupId(state: RootState) {
    return state.transcript_translations.active_translation_scene_group_id;
}

export function getTranslatedEditorBySceneGroupId(
    state: RootState,
    sceneGroupId: string,
): LexicalEditor | undefined {
    return _getTranslatedEditorBySceneGroupId(getTranslatedEditors(state), sceneGroupId);
}

function _getTranslatedEditorBySceneGroupId(
    translatedEditors: StoreORMInterface<TranslatedEditorsType, string>,
    sceneGroupId: string,
): LexicalEditor | undefined {
    const orm = new StoreORM<TranslatedEditorsType, string>(translatedEditors);

    // not every scene group has translations
    if (!orm.getByIdOrUndefined(sceneGroupId)) {
        return;
    }

    const translations = orm.getById(sceneGroupId).get('translations');
    const langCode = _getLangCodeFromSceneGroupId(translatedEditors, sceneGroupId);
    if (!langCode) {
        return;
    }
    const translatedEditor = translations.get(langCode);

    // have to typecast here because it's not actually possible to Immutable-ize the lexical editor
    // instance - but our ImmutableMap type can't handle non-immutables inside of Immutables
    return translatedEditor.get('editor') as unknown as LexicalEditor;
}
